import { db, collection, query, orderBy, getDocs, doc, setDoc, updateDoc, deleteDoc } from '@/store/firebase.config';

const COLLECTION = 'teamio-nudges';
const LS_KEY = 'teamio-dismissed-nudges';

function getDismissedIds() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY)) || [];
  } catch (e) { return []; }
}

// Stable per-nudge key derived from the title. Used for dismissal tracking
// in place of the Firestore doc ID, which includes a Date.now() suffix at
// create time and therefore changes whenever a logically-equivalent nudge
// is re-created (e.g., the seed is re-run or an admin recreates a tip).
// Before this, dismissed entries became orphaned and the same tip would
// reappear after each re-seed/recreate — that's the bug we're fixing.
function dismissKeyOf(nudge) {
  if (!nudge || !nudge.title) return '';
  return nudge.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// Active tips for this route / role / site, lowest priority number first. Dismissals are NOT applied
// here: the page card (nudgesForPage) honours them, the sidebar Tips menu (tipsForPage) does not.
function audienceMatches(state, { route, role, siteID }) {
  return state.nudges
    .filter(n => n.active)
    .filter(n => n.showOnRoutes && n.showOnRoutes.includes(route))
    .filter(n => !n.targetRoles || n.targetRoles.length === 0 || n.targetRoles.includes(role))
    .filter(n => !n.targetSiteIDs || n.targetSiteIDs.length === 0 || n.targetSiteIDs.includes(siteID))
    .sort((a, b) => (a.priority || 99) - (b.priority || 99));
}

export default {
  namespaced: true,
  state: {
    nudges: [],
    loaded: false,
    dismissedIds: getDismissedIds(),
  },
  getters: {
    // Every tip for the page, whether or not it was dismissed from the page card: the sidebar Tips
    // menu is something a person opens on purpose, so a tip they once closed is still there to read.
    tipsForPage: (state) => (ctx) => audienceMatches(state, ctx).slice(0, 8),
    nudgesForPage: (state) => (ctx) => {
      return audienceMatches(state, ctx)
        // Honor BOTH the new stable dismissKey AND any legacy doc-IDs still
        // sitting in localStorage from before this change. Either match
        // means the user already opted out.
        .filter(n => !state.dismissedIds.includes(dismissKeyOf(n)) && !state.dismissedIds.includes(n.id))
        .slice(0, 5);
    },
    allNudges: (state) => state.nudges,
  },
  mutations: {
    setNudges(state, nudges) {
      state.nudges = nudges;
      state.loaded = true;
    },
    setDismissedIds(state, ids) {
      state.dismissedIds = ids;
    },
  },
  actions: {
    async fetchNudges({ commit }) {
      const q = query(collection(db, COLLECTION), orderBy('priority', 'asc'));
      const snapshot = await getDocs(q);
      const nudges = [];
      snapshot.forEach(d => nudges.push({ id: d.id, ...d.data() }));
      commit('setNudges', nudges);
    },

    async createNudge({ dispatch }, nudge) {
      const id = nudge.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '') + '-' + Date.now();
      await setDoc(doc(db, COLLECTION, id), {
        ...nudge,
        createdAt: new Date(),
      });
      await dispatch('fetchNudges');
      return id;
    },

    async updateNudge({ dispatch }, { id, ...data }) {
      await updateDoc(doc(db, COLLECTION, id), data);
      await dispatch('fetchNudges');
    },

    async deleteNudge({ dispatch }, id) {
      await deleteDoc(doc(db, COLLECTION, id));
      await dispatch('fetchNudges');
    },

    // Accepts either a nudge object (preferred — derives the stable key from
    // the title) or a string ID (legacy — looks up the nudge in state).
    // Stores the stable dismissKey rather than the doc ID so dismissals
    // survive re-seeds / recreates that change the underlying doc ID.
    dismissNudge({ commit, state }, nudgeOrId) {
      let key = '';
      if (nudgeOrId && typeof nudgeOrId === 'object') {
        key = dismissKeyOf(nudgeOrId);
      } else if (typeof nudgeOrId === 'string') {
        const found = state.nudges.find(n => n.id === nudgeOrId);
        key = found ? dismissKeyOf(found) : nudgeOrId; // fall back to id if not found
      }
      if (!key || state.dismissedIds.includes(key)) return;
      const updated = [...state.dismissedIds, key];
      localStorage.setItem(LS_KEY, JSON.stringify(updated));
      commit('setDismissedIds', updated);
    },
  },
};
